import React, { useState } from 'react';
import { Button, Space, Input, InputNumber, Form, Select, Row, Col, Tag, Typography, notification, Modal, Popconfirm } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import { TableCustom } from '../../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { deletePremiumTypeRequest, getListPremiumTypesRequest, createPremiumTypeRequest, updatePremiumTypeRequest, listActivePremiumTypesRequest } from '../../../services/cms/premiumTypeServiceRequest';
import { listAllActiveServicesRequest } from '../../../services/cms/serviceServiceRequest';
import { newObjectKeys } from '../../../utils/utility';
import UploadButton from '../../../components/UploadButton';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const QuanLyGoiPremium = () => {
  const [formSearch] = Form.useForm();
  const [formEdit] = Form.useForm();
  const [params, setParams] = useState({ page: 1, limit: 20 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const { data: dataPremiumTypes, refetch } = useQuery({
    queryKey: ['dataPremiumTypes', params],
    queryFn: () => getListPremiumTypesRequest(params),
  });

  const { data: activeServices } = useQuery({
    queryKey: ['allActiveServicesForPremium'],
    queryFn: () => listAllActiveServicesRequest({}),
  });



  const { data: allPremiumTypes } = useQuery({
    queryKey: ['allPremiumTypes'],
    queryFn: () => listActivePremiumTypesRequest({}),
  });

  const { mutate: mutateDelete } = useMutation({
    mutationFn: deletePremiumTypeRequest,
    onSuccess: (data) => {
      if (data?.status === 1) { notification.success({ message: 'Xóa gói premium thành công!' }); refetch(); }
      else notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
    },
  });

  const { mutate: mutateCreate, isPending: isCreating } = useMutation({
    mutationFn: createPremiumTypeRequest,
    onSuccess: (data) => {
      if (data?.status === 1) { notification.success({ message: 'Tạo gói premium thành công!' }); setModalOpen(false); formEdit.resetFields(); refetch(); }
      else notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
    },
  });

  const { mutate: mutateUpdate, isPending: isUpdating } = useMutation({
    mutationFn: updatePremiumTypeRequest,
    onSuccess: (data) => {
      if (data?.status === 1) { notification.success({ message: 'Cập nhật gói premium thành công!' }); setModalOpen(false); setEditingRecord(null); formEdit.resetFields(); refetch(); }
      else notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
    },
  });

  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    if (values?.name) Object.assign(temp, { name: values.name });
    if (values?.status !== undefined && values?.status !== null) Object.assign(temp, { status: values.status });
    if (values?.service_id) Object.assign(temp, { service_id: values.service_id });
    if (values?.platform) Object.assign(temp, { platform: values.platform });
    setParams(temp);
  };

  const formPlatform = Form.useWatch('platform', formEdit);

  const openCreate = () => { formEdit.resetFields(); setEditingRecord(null); setModalOpen(true); };
  const openEdit = (record) => {
    setEditingRecord(record);
    formEdit.setFieldsValue({
      ...record,
      place_show_ad: record.place_show_ad != null ? String(record.place_show_ad) : undefined,
      platform: record.platform != null ? String(record.platform) : undefined,
      premium_type_childs: record.premium_type_childs
        ? record.premium_type_childs.split(',').map(Number).filter(Boolean)
        : [],
      offer_for_id: record.offer_for_id === 0 ? undefined : record.offer_for_id,
    });
    setModalOpen(true);
  };
  const openCopy = (record) => {
    setEditingRecord(null);
    formEdit.setFieldsValue({
      ...record,
      id: undefined,
      name: `[Copy] ${record.name || ''}`,
      place_show_ad: record.place_show_ad != null ? String(record.place_show_ad) : undefined,
      platform: record.platform != null ? String(record.platform) : undefined,
      premium_type_childs: record.premium_type_childs
        ? record.premium_type_childs.split(',').map(Number).filter(Boolean)
        : [],
      offer_for_id: record.offer_for_id === 0 ? undefined : record.offer_for_id,
    });
    setModalOpen(true);
  };
  const handleModalOk = () => {
    formEdit.validateFields().then(values => {
      const submitValues = {
        ...values,
        premium_type_childs: Array.isArray(values.premium_type_childs)
          ? values.premium_type_childs.join(',')
          : (values.premium_type_childs || ''),
        // DB columns có NOT NULL DEFAULT 0 → chuyển null thành 0
        offer_for_id: values.offer_for_id ?? 0,
        numb_m: values.numb_m ?? 0,
        profiles_added: values.profiles_added ?? 0,
        free_trial_days: values.free_trial_days ?? 0,
        campaign_id: values.campaign_id ?? 0,
        is_expert: values.is_expert ?? 0,
        // Platform: backend lưu dạng integer, FE Select dùng string → ép kiểu
        platform: values.platform !== undefined && values.platform !== null && values.platform !== ''
          ? Number(values.platform)
          : null,
      };
      if (editingRecord) mutateUpdate({ id: editingRecord.id, ...submitValues });
      else mutateCreate(submitValues);
    });
  };

  const PLATFORM_MAP = { 1: 'Android', 2: 'iOS', 3: 'Web' };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Tên', dataIndex: 'name', key: 'name', width: 200 },
    { title: 'Mô tả', dataIndex: 'description', key: 'description', width: 220 },
    { title: 'Tên dịch vụ', dataIndex: 'service_name', key: 'service_name', width: 150, render: v => v || '--' },
    { title: 'Số ngày', dataIndex: 'numb', key: 'numb', width: 80, render: v => v ?? '--' },
    { title: 'Số tháng', dataIndex: 'numb_m', key: 'numb_m', width: 80, render: v => v ?? '--' },
    {
      title: 'Giá gốc', dataIndex: 'original_price', key: 'original_price', width: 110,
      render: (p, r) => p ? `${Number(p).toLocaleString()} ${r.currency || ''}`.trim() : '--',
    },
    {
      title: 'Giá hiện tại', dataIndex: 'price', key: 'price', width: 110,
      render: (p, r) => p ? `${Number(p).toLocaleString()} ${r.currency || ''}`.trim() : '--',
    },
    { title: 'Store ID', dataIndex: 'store_id', key: 'store_id', width: 220, render: v => v || '--' },
    { title: 'Store Offer ID', dataIndex: 'store_offer_id', key: 'store_offer_id', width: 220, render: v => v || '--' },
    { title: 'Tên nhóm store', dataIndex: 'store_group', key: 'store_group', width: 150, ellipsis: true, render: v => v || '--' },
    { title: 'Phiên bản', dataIndex: 'app_version', key: 'app_version', width: 90, render: v => v || '--' },
    {
      title: 'Nền tảng', dataIndex: 'platform', key: 'platform', width: 90,
      render: v => PLATFORM_MAP[v] || v || '--',
    },
    {
      title: 'Trạng thái', dataIndex: 'status', key: 'status', width: 100,
      render: status => {
        const STATUS_MAP = { 0: { label: 'Chờ duyệt', color: 'orange' }, 1: { label: 'Duyệt', color: 'green' }, 2: { label: 'Xóa', color: 'red' } };
        const s = STATUS_MAP[status] || { label: status, color: 'default' };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: 'Thao tác', key: 'action', width: 100, fixed: 'right',
      render: (_, record) => (
        <Space size={0}>
          <Button type="text" size="small" onClick={() => openEdit(record)}><EditOutlined /></Button>
          <Button type="text" size="small" title="Sao chép gói" onClick={() => openCopy(record)}><CopyOutlined /></Button>
          <Popconfirm title="Xóa gói này?" onConfirm={() => mutateDelete({ id: record?.id })} okText="Xóa" cancelText="Hủy">
            <Button type="text" size="small" danger><DeleteOutlined /></Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">Quản lý gói Premium</h1>
        <Button type="primary" onClick={openCreate}><PlusOutlined /> Thêm gói</Button>
      </div>
      <Form name="search_premium_types" form={formSearch} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="name" label={<Text>Tên gói</Text>}>
              <Input placeholder="Nhập tên gói" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="service_id" label={<Text>Dịch vụ</Text>}>
              <Select showSearch allowClear placeholder="Chọn dịch vụ"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={activeServices?.map(s => ({ value: s.id, label: s.name })) || []}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="status" label={<Text>Trạng thái</Text>} initialValue={-1}>
              <Select options={[{ value: -1, label: 'Tất cả' }, { value: 0, label: 'Chờ duyệt' }, { value: 1, label: 'Duyệt' }, { value: 2, label: 'Xóa' }]} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="platform" label={<Text>Nền tảng</Text>}>
              <Select allowClear placeholder="Chọn nền tảng" options={[
                { value: 1, label: 'Android' },
                { value: 2, label: 'iOS' },
                { value: 3, label: 'Web' },
              ]} />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit"><SearchOutlined /> Tìm kiếm</Button>
            {params && Object.values(newObjectKeys(params, ['name', 'status', 'service_id', 'platform'])).some((value) => value) && (
              <Button htmlType="button" onClick={() => { formSearch.resetFields(); setParams({ page: 1, limit: params.limit }); }}>
                <DeleteOutlined /> Xóa lọc
              </Button>
            )}
          </Col>
        </Row>
      </Form>
      <TableCustom columns={columns} dataSource={dataPremiumTypes?.data} totalItem={dataPremiumTypes?.total} params={params} setParams={setParams} scroll={{ x: 1720 }} scrollY={450} />

      <Modal title={editingRecord ? 'Sửa gói Premium' : 'Thêm gói Premium'} open={modalOpen} width={1200}
        onCancel={() => { setModalOpen(false); setEditingRecord(null); formEdit.resetFields(); }}
        onOk={handleModalOk} confirmLoading={isCreating || isUpdating} okText="Lưu" cancelText="Hủy" destroyOnClose
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}>

        <Form form={formEdit} layout="vertical">
          {/* === Thông tin cơ bản === */}
          <Paragraph strong style={{ marginBottom: 8, color: '#1677ff' }}>Thông tin cơ bản</Paragraph>
          <Row gutter={12}>
            <Col span={10}>
              <Form.Item name="name" label="Tên gói" rules={[{ required: true, message: 'Nhập tên gói' }]}>
                <Input placeholder="Tên gói" />
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item name="description" label="Mô tả">
                <Input placeholder="Mô tả" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={5}>
              <Form.Item name="service_id" label="Dịch vụ">
                <Select showSearch allowClear placeholder="Chọn dịch vụ"
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={activeServices?.map(s => ({ value: s.id, label: s.name })) || []}
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="purchase_type" label="Loại gói">
                <Select placeholder="Chọn loại" options={[
                  { value: 1, label: 'Purchased' },
                  { value: 2, label: 'Subcription' },
                  { value: 3, label: 'Consum' },
                  { value: 4, label: 'Subcription NonRenew' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="platform" label="Nền tảng">
                <Select placeholder="Chọn nền tảng" allowClear options={[
                  { value: '', label: 'Tất cả' },
                  { value: '1', label: 'Android' },
                  { value: '2', label: 'iOS' },
                  { value: '3', label: 'Web' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="status" label="Trạng thái" initialValue={1}>
                <Select options={[{ value: 0, label: 'Chờ duyệt' }, { value: 1, label: 'Duyệt' }, { value: 2, label: 'Xóa' }]} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="order_place" label="Thứ tự ưu tiên">
                <InputNumber style={{ width: '100%' }} placeholder="Thứ tự" min={0} />
              </Form.Item>
            </Col>
          </Row>

          {/* === Giá & thời hạn === */}
          <Paragraph strong style={{ marginBottom: 8, marginTop: 16, color: '#1677ff' }}>Giá & Thời hạn</Paragraph>
          <Row gutter={12}>
            <Col span={5}>
              <Form.Item name="price" label="Giá">
                <InputNumber style={{ width: '100%' }} placeholder="Giá" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="original_price" label="Giá gốc">
                <InputNumber style={{ width: '100%' }} placeholder="Giá gốc" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="currency" label="Đơn vị tiền">
                <Input placeholder="VND, USD..." />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="free_trial_days" label="Số ngày dùng thử">
                <InputNumber style={{ width: '100%' }} placeholder="Dùng thử" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="profiles_added" label="Profiles thêm">
                <InputNumber style={{ width: '100%' }} placeholder="Số profile" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={5}>
              <Form.Item name="numb" label="Số ngày">
                <InputNumber style={{ width: '100%' }} placeholder="Số ngày" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="numb_m" label="Số tháng">
                <InputNumber style={{ width: '100%' }} placeholder="Số tháng" />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item name="numb_use" label="Số lần dùng">
                <Input placeholder="Số lần" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="is_expert" label="Là gói chuyên gia">
                <Select placeholder="Chọn" allowClear options={[
                  { value: 1, label: 'Có' },
                  { value: 0, label: 'Không' },
                ]} />
              </Form.Item>
            </Col>
          </Row>

          {/* === Store & App === */}
          <Paragraph strong style={{ marginBottom: 8, marginTop: 16, color: '#1677ff' }}>Store &amp; App</Paragraph>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="store_id" label="Store ID">
                <Input placeholder="Store ID (App Store / Google Play)" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="store_offer_id" label="Store Offer ID">
                <Input placeholder="Store Offer ID" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="store_group" label="Tên nhóm store">
                <Input placeholder="Tên nhóm dịch vụ trên store" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="app_version" label="Phiên bản">
                <Input placeholder="Phiên bản" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={24}>
              <Form.Item name="premium_type_childs" label="Gói ưu đãi đi kèm">
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  placeholder="Chọn gói con"
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={allPremiumTypes?.map(p => ({ value: p.id, label: `[${p.id}] ${p.description || p.name}` })) || []}
                />
              </Form.Item>
            </Col>
          </Row>


          {/* === Gói gốc ưu đãi === */}
          <Paragraph strong style={{ marginBottom: 8, marginTop: 16, color: '#1677ff' }}>Gói gốc ưu đãi</Paragraph>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="offer_for_id" label="Gói gốc ưu đãi">
                <Select
                  showSearch
                  allowClear
                  placeholder="Chọn gói gốc"
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                  options={allPremiumTypes?.map(p => ({ value: p.id, label: `[${p.id}] ${p.description || p.name}` })) || []}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* === Ảnh đã mua trên Web (điều kiện: platform = 3 / Web) === */}
          {formPlatform === '3' && (
            <>
              <Paragraph strong style={{ marginBottom: 8, marginTop: 16, color: '#1677ff' }}>Ảnh đã mua trên Web</Paragraph>
              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item name="img_purchased_web" label="Ảnh đã mua trên web">
                    <UploadButton />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {/* === Nội dung mở rộng === */}
          <Paragraph strong style={{ marginBottom: 8, marginTop: 16, color: '#1677ff' }}>Nội dung mở rộng</Paragraph>
          <Form.Item name="metadata" label="Metadata (JSON)">
            <TextArea rows={20} placeholder="JSON metadata" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default QuanLyGoiPremium;
